import { useState } from "react";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { Button } from "Components/General/Button";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { Form, Input, Select, Row, DatePicker, SelectProps } from "antd";
import { Modal } from "Components/General/Modal/GenericModal";
import Text from "antd/lib/typography/Text";
import { metadataAssistantStore } from "./MetadataAssistantStore";
import { observer } from "mobx-react";
import { dayjs } from "Config/dayjs";
import { isEmpty } from "utils/isEmpty";

interface MetadataAssistantModalProps {
  onEdit: (value: API_UploadMetadata[], files?: API_UploadFile[]) => void;
  onClose: () => void;
}

type CustomRangeList = API_MetadataValueRange & { disabled?: boolean; dependencies?: { [x: string]: string[] } };

export interface FakeMetadataResponse extends Omit<API_Metadata, "rangeList"> {
  rangeList?: CustomRangeList[];
  defaultValue?: string;
}

const renderFormItemContent = (format: string, rangeList: CustomRangeList[]) => {
  if (format.toLowerCase() === "w") {
    const options: SelectProps<string>["options"] = rangeList.map(({ description, value, disabled }) => {
      const textValue = description?.length ? `${value} - ${description}` : `${value}`;
      return { label: textValue, value: value, disabled: disabled };
    });
    return <Select allowClear showSearch={{ optionFilterProp: "children" }} options={options} />;
  }
  if (format.toLowerCase() === "date") {
    return <DatePicker format="L" style={{ width: "100%" }} />;
  }
  return <Input />;
};

export const MetadataAssistantModal: React.FC<MetadataAssistantModalProps> = observer(({ onEdit, onClose }) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [form] = Form.useForm();
  const [data, setData] = useState<FakeMetadataResponse[]>([]);

  const parseData = (entryData: { [x: string]: string }): API_UploadMetadata[] => {
    const newData: API_UploadMetadata[] = [];
    for (const key in entryData) {
      const metadataId = +key.split("-DEFID").pop();
      const metadataValue: string | unknown = entryData[key];
      const metadataInfo = data.find((i) => i.definitionId === metadataId);
      if (metadataValue) {
        newData.push({
          name: metadataInfo.key,
          description: metadataInfo.description,
          id: metadataInfo.definitionId,
          value:
            metadataInfo.format.toLowerCase() === "date"
              ? (metadataValue as dayjs.Dayjs).format("DD.MM.YYYY")
              : (metadataValue as string),
        });
      }
    }
    return newData;
  };

  const handleValueChange = (value: { [x: string]: string }) => {
    if (isEmpty(value)) return;
    const bufferData = [...metadataAssistantStore.filteredMetadata] as FakeMetadataResponse[];
    setData(bufferData);
  };

  const handleAbort = () => {
    setData([]);
    onClose();
  };

  const handleReset = () => {
    form.resetFields();
  };

  const handleSubmit = async (values: { [x: string]: string }) => {
    if (!data.length) {
      handleValueChange(values);
    }
    const formData = parseData(values);
    onEdit(formData, metadataAssistantStore.files);
    onClose();
  };

  const { initialMetadataValues } = metadataAssistantStore;

  const infoText =
    metadataAssistantStore.files.length === 1 ? (
      <Text strong>
        * {t("Following file is edited")}: {metadataAssistantStore.files[0]?.name}
      </Text>
    ) : (
      <Text strong>* {t("All files with missing metadata are edited")}</Text>
    );

  return (
    <Modal
      maskClosable={true}
      destroyOnHidden={true}
      title={t("Add required manual meta data")}
      open={metadataAssistantStore.isVisible}
      width={"100%"}
      style={{ maxWidth: 800, top: 20 }}
      footer={null}
      onCancel={handleAbort}
    >
      <Row justify="start">
        <div style={{ marginBottom: 30 }}>
          <div>{infoText}</div>
        </div>
      </Row>
      <Form
        form={form}
        layout="vertical"
        initialValues={initialMetadataValues}
        onFinish={handleSubmit}
        onValuesChange={(changedValue) => handleValueChange(changedValue)}
      >
        {(metadataAssistantStore.filteredMetadata as FakeMetadataResponse[]).map(
          ({ definitionId, description, key, rangeList, format }) => {
            const labelValue = description?.length ? `${key} - ${description}` : `${key}`;
            return (
              <Form.Item
                key={definitionId}
                name={`${key}-DEFID${definitionId}`}
                label={labelValue}
                rules={[{ required: true, message: t("Please enter a value") }]}
              >
                {renderFormItemContent(format, rangeList)}
              </Form.Item>
            );
          }
        )}
        <ButtonsRow justify="space-between" style={{ marginTop: 30 }}>
          <Button onClick={handleAbort}>{t("Abort")}</Button>
          <div>
            <Button onClick={handleReset} style={{ marginRight: 10 }}>
              {t("Reset")}
            </Button>
            <Button type="primary" htmlType="submit">
              {t("Confirm")}
            </Button>
          </div>
        </ButtonsRow>
      </Form>
    </Modal>
  );
});
